import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../../Modal';
import { Button, Input, ScrollArea, Switch, Typography } from '../ui';
import { devToolsUnlocked } from '../../../lib/dev-tools';
import { fuzzyFilter } from '../../../lib/fuzzy-match';
import {
  clearToggleOverrides,
  isOverridePersisted,
  setToggleOverride,
  toggleOverride,
  WEB_TOGGLES,
  type ToggleOverride,
} from '../../../lib/dev-feature-toggles';
import { API_BASE } from '../../../lib/api-base';
import styles from './DevFeatureTogglesPanel.module.css';

// Web port of the native apps' Dev Tools ▸ Feature Toggles screen — same rows, same tri-state, same
// Persist switch (see lib/dev-feature-toggles.ts for the model, and the iOS/Android screens it mirrors).
//
// The Default pill carries the NATURAL conducted state ("Default · On"/"Default · Off"), probed from the
// server's own conduct route on open. That probe deliberately bypasses `instanceAuthEnabled()` — that
// helper honours the override, so asking it would report what we forced, not what Petri conducted.

/** The conducted (non-overridden) value per spec, `undefined` while probing or if the probe failed. */
type Conducted = Record<string, boolean | undefined>;

/**
 * Where each toggle's natural value is PROBED FROM — our own `/api/flags/*` route, not the experiment
 * system. Despite the name this is system-agnostic: the route behind each entry owns the choice between
 * Petri's laboratory and conductor-server, and both kinds sit in this one map (`specs.symphony.*` and
 * `symphonyPersonalSaleMobile` are Petri; `symphonyInstanceAuthWeb` and friends are Conductor). There is
 * deliberately no second Petri-only map — that would duplicate a decision the routes already own, and
 * the two copies would drift.
 *
 * Every entry is read as a BOOLEAN, so this shape fits an on/off rollout. A true multi-arm A/B test
 * would need its route to return the group's value rather than `true`/`false`, and the tri-state below
 * to become a group picker.
 *
 * Kept beside the registry it belongs to.
 */
const CONDUCT_ENDPOINT: Record<string, string> = {
  symphonyInstanceAuthWeb: '/api/flags/instance-auth',
  'specs.orion.SymphonyBillingCycleStep': '/api/flags/billing-cycle-step',
  symphonyDailyCreditsLimit: '/api/flags/daily-limit-bar',
  symphonyChangePaymentMethod: '/api/flags/change-payment-method',
  // These three were missing, so their Default pills have been reading "Default · ?" since each
  // shipped — every route exists and serves a bare boolean, they were simply never registered.
  // `test/daily-limit-bar-gate.test.ts` now fails on a WEB_TOGGLES entry with no endpoint.
  symphonyVoiceCallWeb: '/api/flags/voice-call',
  symphonyOutOfCreditsEarnOfferEnabled: '/api/flags/earn-credits-offer',
  alfredInSymphonyOutreach: '/api/flags/alfred-in-outreach',
  alfredOnboardingInSymphony: '/api/flags/alfred-onboarding',
  alfredSettingsInSymphony: '/api/flags/alfred-settings',
  symphonyOutreachCallTab: '/api/flags/outreach-call-tab',
  'specs.symphony.SymphonyAgentsWorldHomeWeb': '/api/flags/agents-world-home',
  'specs.symphony.SymphonyActivityMapHomeWeb': '/api/flags/activity-map-home',
  'specs.symphony.SymphonyActivityMapHideTabsWeb': '/api/flags/activity-map-hide-tabs',
  symphonyWhatsNew: '/api/flags/whats-new',
  symphonyCalendar: '/api/flags/calendar',
  symphonyChatFilePaneWeb: '/api/flags/chat-file-pane',
  symphonyChatStatusPanelWeb: '/api/flags/chat-status-panel',
  symphonyConnectWalkPopupWeb: '/api/flags/connect-walk-popup',
  symphonyPersonalSaleMobile: '/api/flags/personal-sale-mobile',
  symphonyMeetingsAgent: '/api/flags/meetings-agent',
  symphonyCreditMinerWeb: '/api/flags/credit-miner',
  symphonyProfileLinks: '/api/flags/profile-links',
  'specs.symphony.BlackFridayCampaign': '/api/flags/black-friday-campaign',
};

/** Gate #3 (BrandHeader and DevMenu check it too): never renders without a dev build or a grant. */
export function DevFeatureTogglesPanel(props: { open: boolean; onClose: () => void }) {
  if (!devToolsUnlocked()) return null;
  return <FeatureTogglesPanel {...props} />;
}

function FeatureTogglesPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [conducted, setConducted] = useState<Conducted>({});
  const [probing, setProbing] = useState(false);
  // Bumped after every write so the rows re-read the store — native's `refreshTick`.
  const [tick, setTick] = useState(0);
  // Per-row Persist intent: the dev's choice for the NEXT On/Off tap when no override is active yet.
  const [persistChoice, setPersistChoice] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState('');

  // The spec id only — the one field native's screen lists, so one query ranks all three the same.
  const visible = useMemo(() => fuzzyFilter(WEB_TOGGLES, query, (toggle) => toggle.spec), [query]);

  useEffect(() => {
    if (!open) return;
    setProbing(true);
    let cancelled = false;
    Promise.all(
      WEB_TOGGLES.map(async ({ spec }) => {
        const endpoint = CONDUCT_ENDPOINT[spec];
        if (!endpoint) return [spec, undefined] as const;
        try {
          const res = await fetch(`${API_BASE}${endpoint}`, { credentials: 'include' });
          return [spec, res.ok ? ((await res.json()) as boolean) === true : undefined] as const;
        } catch {
          return [spec, undefined] as const;
        }
      }),
    )
      .then((pairs) => {
        if (!cancelled) setConducted(Object.fromEntries(pairs));
      })
      .finally(() => {
        if (!cancelled) setProbing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  /** The effective Persist value: an explicit choice, else the store's flag, else the default (ON). */
  const persistOn = useCallback(
    (spec: string): boolean => persistChoice[spec] ?? (toggleOverride(spec) !== undefined ? isOverridePersisted(spec) : true),
    [persistChoice, tick], // eslint-disable-line react-hooks/exhaustive-deps -- tick forces a re-read of the store
  );

  const choose = (spec: string, value: ToggleOverride | null) => {
    setToggleOverride(spec, value, persistOn(spec));
    setTick((n) => n + 1);
  };

  const setPersist = (spec: string, next: boolean) => {
    setPersistChoice((prev) => ({ ...prev, [spec]: next }));
    // Flipping Persist while an override is active re-applies it to the other store — same as native.
    const current = toggleOverride(spec);
    if (current !== undefined) setToggleOverride(spec, current, next);
    setTick((n) => n + 1);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={620}
      title="Feature toggles"
      footer={
        <>
          <Button
            variant="outlined"
            onClick={() => {
              clearToggleOverrides();
              window.location.reload();
            }}
          >
            Use conducted values
          </Button>
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </>
      }
    >
      <Typography variant="p3" color="secondary">
        Force a flag On or Off in THIS browser. <strong>Default</strong> uses the value Petri conducts for you; the label
        shows what that currently is. <strong>Persist</strong> keeps the override across reloads — turn it off and it
        dies with this tab. Reload after changing one so every reader picks it up.
      </Typography>

      <Input
        kind="search"
        className={styles.search}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onClear={() => setQuery('')}
        clearAriaLabel="Clear search"
        placeholder="Search specs"
        aria-label="Search feature toggles"
      />

      <ScrollArea className={styles.list}>
        {visible.length === 0 ? (
          <Typography variant="p3" color="muted" className={styles.empty}>
            No toggle matches “{query}”.
          </Typography>
        ) : null}
        {visible.map(({ spec, description }) => {
          const override = toggleOverride(spec);
          const natural = conducted[spec];
          const defaultLabel = probing
            ? 'Default'
            : natural === undefined
              ? 'Default · ?'
              : `Default · ${natural ? 'On' : 'Off'}`;

          return (
            <section key={spec} className={styles.card} data-overridden={override !== undefined || undefined}>
              <Typography variant="p3" color="strong" component="div">
                {spec}
              </Typography>
              <Typography variant="p3" color="muted" component="div">
                {description}
              </Typography>

              <div className={styles.pills}>
                <Button
                  size="small"
                  fullWidth
                  variant={override === undefined ? 'contained' : 'outlined'}
                  onClick={() => choose(spec, null)}
                >
                  {defaultLabel}
                </Button>
                <Button
                  size="small"
                  fullWidth
                  variant={override === 'true' ? 'contained' : 'outlined'}
                  onClick={() => choose(spec, 'true')}
                >
                  On
                </Button>
                <Button
                  size="small"
                  fullWidth
                  variant={override === 'false' ? 'contained' : 'outlined'}
                  onClick={() => choose(spec, 'false')}
                >
                  Off
                </Button>
              </div>

              <div className={styles.persist}>
                <Switch checked={persistOn(spec)} onChange={(next) => setPersist(spec, next)} label="Persist" />
              </div>
            </section>
          );
        })}
      </ScrollArea>
    </Modal>
  );
}
